import { configSwatchLiteral } from '@nova/configs';
import {
  BotPartsConfig,
  BotPartSlot,
  CompletePartData,
  CompletePartDataSet,
  IDynamicPartData,
  IPartConfigData,
  PartDatasetKey
} from '@nova/types';
import { getRandomElement } from '@nova/utils';

import { BotOptions } from '../bot-defaults';
import { ORDERED_BOT_PART_SLOTS } from '../bot-slots';

export function resolveSlotsToRandomize(options?: BotOptions): BotPartSlot[] {
  if (options?.slotsToRandomize?.length) {
    return options.slotsToRandomize;
  }

  if (options?.visibleSlots?.length) {
    return options.visibleSlots;
  }

  return [...ORDERED_BOT_PART_SLOTS];
}

function shouldRandomizeSlot(slot: BotPartSlot, slotsToRandomize: BotPartSlot[]): boolean {
  return slotsToRandomize.includes(slot);
}

function getRandomSwatchKey(): string {
  return getRandomElement(Object.keys(configSwatchLiteral));
}

function getRandomPartKeyBySlot(
  slot: BotPartSlot,
  defaultsOnly: boolean,
  botPartsConfig: BotPartsConfig
): PartDatasetKey {
  const filteredKeys: PartDatasetKey[] = [];
  for (const part of Object.keys(botPartsConfig)) {
    if (botPartsConfig[part].slot === slot && (!defaultsOnly || botPartsConfig[part].isDefault)) {
      filteredKeys.push(part);
    }
  }

  if (filteredKeys.length === 0) {
    throw new Error(`No bot part found for slot ${slot} (defaultsOnly=${defaultsOnly})`);
  }

  return getRandomElement(filteredKeys);
}

export function createRandomPartData(
  slot: BotPartSlot,
  botPartsConfig: BotPartsConfig,
  defaultsOnly = false
): CompletePartData {
  const partKey = getRandomPartKeyBySlot(slot, defaultsOnly, botPartsConfig);
  const partConfig = botPartsConfig[partKey] as IPartConfigData;

  const dynamicPartData: IDynamicPartData = {
    datasetKey: partKey,
    swatchKey: defaultsOnly ? 'item_swatch_enemywhite' : getRandomSwatchKey(),
    wearTear: 0,
    dirtLevel: 0,
    tintSlots: ['lights', 'eyes']
  };

  return { ...partConfig, ...dynamicPartData };
}

export function createRandomPartDataSet(
  botPartsConfig: BotPartsConfig,
  options?: BotOptions
): CompletePartDataSet {
  const slotsToRandomize = resolveSlotsToRandomize(options);

  return ORDERED_BOT_PART_SLOTS.reduce<CompletePartDataSet>((partSet, slot) => {
    partSet[slot] = createRandomPartData(
      slot,
      botPartsConfig,
      !shouldRandomizeSlot(slot, slotsToRandomize)
    );
    return partSet;
  }, {} as CompletePartDataSet);
}
